import { useDispatch, useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { ExportVariantsDropdownItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { initExportToCrmAs } from "app/pages/sales-intelligence/sub-modules/company-search/store/action-creators";

import ExportToDynamicsDropdownItem from "../../components/ExportToDynamicsDropdownItem/ExportToDynamicsDropdownItem";
import {
    selectIsDynamicsCompanyAsLeadExportClaimEnabled,
    selectDynamicsCompanyAsLeadsToggleState,
    selectIsDynamicsExportEnabled,
    selectDynamicsStatus,
} from "../../store/selectors/dynamicsSelectors";
import {
    EXPORT_DYNAMICS_BTN,
    EXPORT_INTEGRATION_TOOLTIP,
    EXPORT_DYNAMICS_ACCOUNT_BTN,
    EXPORT_DYNAMICS_LEAD_BTN,
    CONNECT_TO_INTEGRATION_TOOLTIP,
    ADMIN_ENABLE_EXPORT_INTEGRATION_TOOLTIP,
    CRM_IS_NOT_CONNECTED_TOOLTIP,
} from "../../translations/ExportItem";
import { CrmListObjectType } from "../../types";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

type WebsitesDynamicsExportItemContainerProps = {
    onClick: () => void;
    tooltipDisabledContent?: string;
    isUseLockedIcon?: boolean;
};

const WebsitesDynamicsExportItemContainer = ({
    onClick,
    tooltipDisabledContent,
    isUseLockedIcon = true,
}: WebsitesDynamicsExportItemContainerProps) => {
    const dispatch = useDispatch();
    const syncStatus = useSelector(selectDynamicsStatus);
    const isExportEnabled = useSelector(selectIsDynamicsExportEnabled);
    const isCompanyAsLeadExportClaimEnabled = useSelector(selectIsDynamicsCompanyAsLeadExportClaimEnabled);
    const isCompanyAsLeadExportEnabled = useSelector(selectDynamicsCompanyAsLeadsToggleState);

    const redirectToManagementPage = () => {
        void siNavigator.goToManagementPage({
            isScrollToIntegrationSection: true,
        });
    };

    const isAdmin = salesSettingsHelper.isAdmin();

    const handleExportAccount = () => {
        dispatch(initExportToCrmAs(CrmListObjectType.Accounts));
        onClick();
    };

    const handleExportLead = () => {
        dispatch(initExportToCrmAs(CrmListObjectType.Leads));
        onClick();
    };

    if (tooltipDisabledContent) {
        return (
            <ExportVariantsDropdownItem
                iconName={ExportIntegrations.dynamics}
                isDisabled
                variant={ExportIntegrations.dynamics}
                text={translate(EXPORT_DYNAMICS_BTN)}
                tooltipDisabledContent={tooltipDisabledContent}
                isUseLockedIcon={isUseLockedIcon}
            />
        );
    }

    if (syncStatus === "NotIntegrated") {
        if (isAdmin) {
            return (
                <ExportVariantsDropdownItem
                    iconName={ExportIntegrations.dynamics}
                    isDisabled={false}
                    variant={ExportIntegrations.dynamics}
                    text={translate(CONNECT_TO_INTEGRATION_TOOLTIP, { integrationName: "Dynamics 365" })}
                    isUseLockedIcon={isUseLockedIcon}
                    onClick={redirectToManagementPage}
                />
            );
        } else {
            return (
                <ExportVariantsDropdownItem
                    iconName={ExportIntegrations.dynamics}
                    isDisabled={true}
                    variant={ExportIntegrations.dynamics}
                    text={translate(EXPORT_DYNAMICS_ACCOUNT_BTN)}
                    isUseLockedIcon={isUseLockedIcon}
                    tooltipDisabledContent={translate(CRM_IS_NOT_CONNECTED_TOOLTIP)}
                />
            );
        }
    }

    if (isAdmin && !isExportEnabled) {
        return (
            <ExportVariantsDropdownItem
                iconName={ExportIntegrations.dynamics}
                isDisabled={false}
                variant={ExportIntegrations.dynamics}
                text={translate(EXPORT_DYNAMICS_BTN)}
                isUseLockedIcon={isUseLockedIcon}
                tooltipContent={translate(ADMIN_ENABLE_EXPORT_INTEGRATION_TOOLTIP)}
                onClick={redirectToManagementPage}
            />
        );
    }

    if (!isExportEnabled && !isCompanyAsLeadExportEnabled) {
        return (
            <ExportVariantsDropdownItem
                iconName={ExportIntegrations.dynamics}
                isDisabled
                variant={ExportIntegrations.dynamics}
                text={translate(EXPORT_DYNAMICS_BTN)}
                tooltipDisabledContent={translate(EXPORT_INTEGRATION_TOOLTIP, {
                    integrationName: "Dynamics 365",
                })}
                isUseLockedIcon={isUseLockedIcon}
            />
        );
    }

    if (isExportEnabled && isCompanyAsLeadExportClaimEnabled && isCompanyAsLeadExportEnabled) {
        return (
            <>
                <ExportToDynamicsDropdownItem
                    text={`${translate(EXPORT_DYNAMICS_BTN)}:`}
                    isUseLockedIcon={isUseLockedIcon}
                />
                <ExportVariantsDropdownItem
                    variant="accounts"
                    text={translate(EXPORT_DYNAMICS_ACCOUNT_BTN)}
                    onClick={handleExportAccount}
                    isUseLockedIcon={isUseLockedIcon}
                />
                <ExportVariantsDropdownItem
                    variant="leads"
                    text={translate(EXPORT_DYNAMICS_LEAD_BTN)}
                    onClick={handleExportLead}
                    isUseLockedIcon={isUseLockedIcon}
                />
            </>
        );
    }

    return (
        <ExportVariantsDropdownItem
            iconName={ExportIntegrations.dynamics}
            variant={ExportIntegrations.dynamics}
            text={translate(EXPORT_DYNAMICS_BTN)}
            onClick={
                isCompanyAsLeadExportClaimEnabled && isCompanyAsLeadExportEnabled
                    ? handleExportLead
                    : handleExportAccount
            }
            isUseLockedIcon={isUseLockedIcon}
        />
    );
};

export default WebsitesDynamicsExportItemContainer;
